import { useRouter } from 'next/router';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { filterUrlQueryParams } from 'constants/category-filters';
import PreserveScrollContext from 'context/PreserveScrollContext';

const SCROLL_SHOW_THRESHOLD = 120;
const SCROLL_HIDE_THRESHOLD = 20;

const PreserveScrollProvider = ({ children }: { children: ReactNode }) => {
  const router = useRouter();
  const lastScrollY = useRef<number>(0);
  const previousUrlRef = useRef<string>('');
  const navigatingRef = useRef<boolean>(false);

  const [itemId, setItemId] = useState(() => {
    const value = router?.query?.[filterUrlQueryParams.ITEM_ID];
    return typeof value === 'string' ? value : '';
  });
  const [isScrolled, setIsScrolled] = useState(false);

  // Hydrate itemId from URL query on mount
  useEffect(() => {
    if (!router?.isReady) return;

    const queryItemId = router?.query?.[filterUrlQueryParams.ITEM_ID];
    if (typeof queryItemId === 'string') {
      setItemId(queryItemId);
    }
  }, [router?.isReady, router?.query]);

  useEffect(() => {
    const currentPathRef = previousUrlRef;

    const handleRouteStart = (url: string) => {
      const nextPath = new URL(url, window?.location?.origin)?.pathname;
      const currentPath = currentPathRef?.current;

      const isSamePage = nextPath === currentPath;

      navigatingRef.current = !isSamePage;
      // Only set isScrolled if navigating to a different page
      if (!isSamePage) {
        setIsScrolled(false);
      }
    };

    const handleRouteDone = () => {
      const newPath = new URL(router?.asPath, window?.location?.origin)?.pathname;
      previousUrlRef.current = newPath;
      navigatingRef.current = false;
    };

    // Initialize currentPath
    previousUrlRef.current = new URL(router?.asPath, window?.location?.origin)?.pathname;

    router.events.on('routeChangeStart', handleRouteStart);
    router.events.on('routeChangeComplete', handleRouteDone);
    router.events.on('routeChangeError', handleRouteDone);

    return () => {
      router.events.off('routeChangeStart', handleRouteStart);
      router.events.off('routeChangeComplete', handleRouteDone);
      router.events.off('routeChangeError', handleRouteDone);
    };
  }, [router]);

  useEffect(() => {
    const handleScroll = () => {
      if (navigatingRef?.current) return;

      const currentScroll = window?.scrollY;

      if (currentScroll < SCROLL_HIDE_THRESHOLD && isScrolled) {
        setIsScrolled(false);
      } else if (currentScroll > SCROLL_SHOW_THRESHOLD && !isScrolled) {
        setIsScrolled(true);
      }

      lastScrollY.current = currentScroll;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [navigatingRef, isScrolled]);

  const updateItemId = (newItemId: string) => setItemId(newItemId);

  const state = useMemo(
    () => ({
      itemId,
      isScrolled,
      updateItemId,
    }),
    [isScrolled, itemId]
  );

  return <PreserveScrollContext.Provider value={state}>{children}</PreserveScrollContext.Provider>;
};

export default PreserveScrollProvider;
